import React from 'react';

const AIOptimization = () => {
  return (
    <section id="ai-optimization" className="py-20 bg-gradient-to-br from-orange-50 via-white to-orange-50">
      <style>{`
        @keyframes drawRoute {
          0% { stroke-dashoffset: 1000; }
          100% { stroke-dashoffset: 0; }
        }
        @keyframes float {
          0%, 100% { transform: translate(160px, 60px); }
          50% { transform: translate(160px, 50px); }
        }
        @keyframes pulse-ring {
          0% { transform: scale(0.8); opacity: 0.5; }
          100% { transform: scale(1.2); opacity: 0; }
        }
        @keyframes dash {
          to { stroke-dashoffset: -20; }
        }
        .animate-draw {
          stroke-dasharray: 1000;
          stroke-dashoffset: 1000;
          animation: drawRoute 2s ease-out forwards;
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        .animate-dash {
          animation: dash 1s linear infinite;
        }
        // .pulse-circle {
        //   transform-origin: center;
        //   animation: pulse-ring 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        // }
      `}</style>
      <div className="container mx-auto">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          
          {/* Image à gauche */}
          <div className="w-full lg:w-1/2 animate-on-scroll">
            <div className="relative">
              {/* Illustration AI/Route Optimization */}
              <svg viewBox="0 0 400 320" className="w-full max-w-md mx-auto" xmlns="http://www.w3.org/2000/svg">
                {/* Background circles */}
                <circle cx="200" cy="160" r="140" fill="#FFF7ED" />
                <circle cx="200" cy="160" r="100" fill="#FFEDD5" />
                
                {/* Route lines */}
                <path className="animate-dash" d="M80 200 Q120 140 200 160 Q280 180 320 120" stroke="#F97316" strokeWidth="3" fill="none" strokeDasharray="8 4" opacity="0.6"/>
                <path className="animate-dash" style={{ animationDuration: '1.5s', animationDirection: 'reverse' }} d="M100 240 Q160 200 200 160 Q240 120 300 100" stroke="#F97316" strokeWidth="3" fill="none" strokeDasharray="8 4" opacity="0.4"/>
                
                {/* Optimized route */}
                <path className="animate-draw" d="M80 200 Q140 160 200 160 Q260 160 320 120" stroke="#F97316" strokeWidth="4" fill="none"/>
                
                {/* Location pins */}
                <g transform="translate(70, 185)">
                  <circle cx="10" cy="10" r="16" fill="#F97316" opacity="0.2" className="pulse-circle"/>
                  <circle cx="10" cy="10" r="12" fill="#F97316"/>
                  <circle cx="10" cy="10" r="5" fill="white"/>
                </g>
                <g transform="translate(190, 145)">
                  <circle cx="10" cy="10" r="14" fill="#F97316"/>
                  <path d="M10 5 L10 15 M5 10 L15 10" stroke="white" strokeWidth="2"/>
                </g>
                <g transform="translate(310, 105)">
                  <circle cx="10" cy="10" r="16" fill="#22C55E" opacity="0.2" className="pulse-circle" style={{ animationDelay: '0.5s' }}/>
                  <circle cx="10" cy="10" r="12" fill="#22C55E"/>
                  <path d="M6 10 L9 13 L14 7" stroke="white" strokeWidth="2" fill="none"/>
                </g>
                
                {/* AI Brain icon */}
                <g className="animate-float">
                  <rect x="0" y="0" width="80" height="50" rx="8" fill="white" filter="drop-shadow(0 4px 6px rgba(0,0,0,0.1))"/>
                  <text x="40" y="22" textAnchor="middle" fontSize="10" fill="#F97316" fontWeight="bold">AI</text>
                  <text x="40" y="38" textAnchor="middle" fontSize="8" fill="#6B7280">Optimizing...</text>
                </g>
                
                {/* Floating elements */}
                <circle cx="320" cy="200" r="6" fill="#F97316" opacity="0.3"/>
                <circle cx="100" cy="100" r="4" fill="#F97316" opacity="0.4"/>
                <circle cx="280" cy="240" r="5" fill="#F97316" opacity="0.2"/>
              </svg>
            </div>
          </div>

          {/* Texte à droite */}
          <div className="w-full lg:w-1/2 text-center lg:text-left flex flex-col items-center lg:items-start max-w-2xl mx-auto lg:mx-0 lg:max-w-xl">

            <h2 className="text-3xl md:text-4xl font-bold mb-4 animate-on-scroll">
              Optimisation <span className="text-primary-custom">intelligente</span>
            </h2>
            
            <p className="text-lg text-gray-600 mb-8 animate-on-scroll">
              Un moteur d’optimisation alimenté par l’IA pour calculer les itinéraires optimaux et regrouper vos livraisons pour réduire les coûts et améliorer la ponctualité.
            </p>

            {/* Features list */}
            <div className="space-y-5 animate-on-scroll w-full max-w-md mx-auto lg:max-w-none lg:mx-0">
              <div className="flex flex-col items-center text-center lg:flex-row lg:text-left lg:items-start gap-3 lg:gap-4">
                <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#F97316" strokeWidth="2"/>
                    <path d="M12 6V12L16 14" stroke="#F97316" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Livraisons plus rapides</h3>
                  <p className="text-gray-500 text-sm">Itinéraires calculés en temps réel</p>
                </div>
              </div>

              <div className="flex flex-col items-center text-center lg:flex-row lg:text-left lg:items-start gap-3 lg:gap-4">
                <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 11L12 14L22 4" stroke="#F97316" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M21 12V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V5C3 3.89543 3.89543 3 5 3H16" stroke="#F97316" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Regroupement intelligent</h3>
                  <p className="text-gray-500 text-sm">Colis groupés automatiquement par zone</p>
                </div>
              </div>

              <div className="flex flex-col items-center text-center lg:flex-row lg:text-left lg:items-start gap-3 lg:gap-4">
                <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 1V23" stroke="#F97316" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M17 5H9.5C7.567 5 6 6.567 6 8.5C6 10.433 7.567 12 9.5 12H14.5C16.433 12 18 13.567 18 15.5C18 17.433 16.433 19 14.5 19H6" stroke="#F97316" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Coûts réduits</h3>
                  <p className="text-gray-500 text-sm">Moins de détours = tarifs compétitifs</p>
                </div>
              </div>
            </div>

            <div className="mt-10 animate-on-scroll">
              <a 
                href="/get-quote" 
                className="inline-flex items-center justify-center px-8 py-3 text-base font-medium text-white bg-black rounded-lg hover:bg-gray-800 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                Simuler une livraison
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </a>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default AIOptimization;
